import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Link,
  Modal,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-react";
import {
  ChatUatContextReference,
  ProjectValidationCase,
  ProjectValidationFieldResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import useUatProjectValidation from "../../hooks/useUatProjectValidation";
import ChatUatPage, { ChatUatPageHandle } from "./ChatUatPage";
import "./chatUat.scss";

/* AWSUI table cell callbacks are render functions, not component types. */
/* eslint-disable react/no-unstable-nested-components */

interface UatScopeValidationPageProps {
  onInvestigate: (reference: ChatUatContextReference) => void;
}

function verdictOrder(item: ProjectValidationCase): number {
  return {
    wrong_order: 0,
    accounting_mismatch: 1,
    cannot_verify: 2,
    incomplete_scope: 3,
    matches_scope: 4,
  }[item.assessment];
}

function verdictIndicator(item: ProjectValidationCase): React.ReactNode {
  if (item.assessment === "wrong_order") {
    return <StatusIndicator type="error">Wrong order</StatusIndicator>;
  }
  if (item.assessment === "accounting_mismatch") {
    return <StatusIndicator type="error">Accounting mismatch</StatusIndicator>;
  }
  if (item.assessment === "incomplete_scope") {
    return <StatusIndicator type="warning">Incomplete scope</StatusIndicator>;
  }
  if (item.assessment === "cannot_verify") {
    return <StatusIndicator type="warning">Cannot verify</StatusIndicator>;
  }
  return (
    <StatusIndicator type="success">Matches intended test</StatusIndicator>
  );
}

function fieldIndicator(
  verdict: ProjectValidationFieldResult["verdict"]
): React.ReactNode {
  if (verdict === "mismatch") {
    return <StatusIndicator type="error">Mismatch</StatusIndicator>;
  }
  if (verdict === "not_verifiable") {
    return <StatusIndicator type="warning">Not verifiable</StatusIndicator>;
  }
  if (verdict === "not_applicable") {
    return <StatusIndicator type="stopped">Not applicable</StatusIndicator>;
  }
  return <StatusIndicator type="success">Match</StatusIndicator>;
}

function displayValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "Not available";
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(" → ");
  }
  return String(value);
}

function nextStep(item: ProjectValidationCase): string {
  if (item.assessment === "wrong_order") {
    return "Retest with correct order";
  }
  if (item.assessment === "accounting_mismatch") {
    return "Investigate accounting";
  }
  if (item.assessment === "incomplete_scope") {
    return "Complete Asana task";
  }
  if (item.assessment === "cannot_verify") {
    return "Review missing evidence";
  }
  return "No action";
}

export default function UatScopeValidationPage({
  onInvestigate,
}: UatScopeValidationPageProps) {
  const [searchParams] = useSearchParams();
  const requestedStage = searchParams.get("stage");
  const stage = Object.values(Stage).includes(requestedStage as Stage)
    ? (requestedStage as Stage)
    : Stage.Prod;
  const workflow = useUatProjectValidation(stage);
  const [editingConnection, setEditingConnection] = useState(false);
  const [disconnectVisible, setDisconnectVisible] = useState(false);
  const [projectReference, setProjectReference] = useState("");
  const [selectedCase, setSelectedCase] = useState<ProjectValidationCase>();
  const chatRef = useRef<ChatUatPageHandle>(null);

  const cases = useMemo(
    () => workflow.selectedRun?.cases ?? [],
    [workflow.selectedRun?.cases]
  );
  const sortedCases = useMemo(
    () =>
      [...cases].sort(
        (left, right) =>
          verdictOrder(left) - verdictOrder(right) ||
          left.taskName.localeCompare(right.taskName)
      ),
    [cases]
  );
  const assessmentCounts = useMemo(
    () =>
      cases.reduce(
        (counts, item) => {
          counts[item.assessment] += 1;
          return counts;
        },
        {
          matches_scope: 0,
          wrong_order: 0,
          accounting_mismatch: 0,
          cannot_verify: 0,
          incomplete_scope: 0,
        }
      ),
    [cases]
  );

  useEffect(() => {
    setSelectedCase(undefined);
  }, [workflow.selectedRunId]);

  const { state } = workflow;
  if (!state && workflow.isLoading) {
    return (
      <Box textAlign="center" padding="xxl">
        <StatusIndicator type="loading">Loading validation</StatusIndicator>
      </Box>
    );
  }
  if (!state) {
    return (
      <Alert type="error" header="Validation unavailable">
        {workflow.error ?? "UAT Insights could not load validation state."}
      </Alert>
    );
  }

  const activeRun =
    workflow.selectedRun?.run ?? state.latestValidationRun ?? workflow.runs[0];
  const jobProgress = workflow.job?.progress;
  const isJobActive = workflow.isStarting || workflow.isPolling;
  const showConnectionEditor = editingConnection || !state.asanaLink;
  const showAuthorization = !workflow.isAsanaAuthorized;

  const previewProject = async () => {
    await workflow.previewAsanaProject(projectReference);
  };

  const saveConnection = async () => {
    if (!workflow.preview) {
      return;
    }
    await workflow.saveAsanaLink({
      projectGid: workflow.preview.project.gid,
      version: state.version,
    });
    setEditingConnection(false);
    setProjectReference("");
  };

  const disconnect = async () => {
    await workflow.disconnectAsana();
    setDisconnectVisible(false);
    setEditingConnection(true);
  };

  const investigate = (item: ProjectValidationCase) => {
    if (!activeRun) {
      return;
    }
    const reference: ChatUatContextReference = {
      runId: activeRun.runId,
      taskGid: item.taskGid,
    };
    if (item.orderId) {
      reference.orderId = item.orderId;
    }
    onInvestigate(reference);
  };

  const fieldRows = selectedCase
    ? Object.entries(selectedCase.fields).sort(([left], [right]) =>
        left.localeCompare(right)
      )
    : [];
  const selectedTaskGid = searchParams.get("taskGid");
  const attentionCount =
    assessmentCounts.wrong_order +
    assessmentCounts.accounting_mismatch +
    assessmentCounts.cannot_verify +
    assessmentCounts.incomplete_scope;

  return (
    <>
      <ContentLayout
        defaultPadding
        header={
          <Header
            variant="h1"
            description="Validate an Asana test case, investigate its order, and prepare a review for sign-off."
            actions={
              <Button onClick={() => chatRef.current?.startNewConversation()}>
                New conversation
              </Button>
            }
          >
            TrackStars UAT Insights
          </Header>
        }
      >
        <SpaceBetween size="l">
          {showAuthorization ? (
            <Container
              header={
                <Header
                  variant="h2"
                  description="Authorize UAT Insights to read the Asana projects available to your account."
                >
                  {workflow.asanaAuthorizationRequired
                    ? "Reconnect Asana"
                    : "Connect Asana"}
                </Header>
              }
            >
              <SpaceBetween size="m">
                {workflow.asanaAuthorizationError && (
                  <Alert type="error">{workflow.asanaAuthorizationError}</Alert>
                )}
                <Box float="right">
                  <Button
                    variant="primary"
                    loading={workflow.isConnectingAsana}
                    onClick={() => {
                      workflow
                        .beginAsanaAuthorization(
                          workflow.asanaAuthorizationRequired
                        )
                        .catch(() => undefined);
                    }}
                  >
                    {workflow.asanaAuthorizationRequired
                      ? "Reconnect Asana"
                      : "Connect Asana"}
                  </Button>
                </Box>
              </SpaceBetween>
            </Container>
          ) : showConnectionEditor ? (
            <Container
              header={
                <Header
                  variant="h2"
                  description="Paste an Asana project to validate its incomplete test-case tasks."
                >
                  {state.asanaLink ? "Change Asana project" : "Connect Asana"}
                </Header>
              }
            >
              <Form errorText={workflow.previewError ?? workflow.saveLinkError}>
                <SpaceBetween size="m">
                  <FormField label="Asana project">
                    <Input
                      value={projectReference}
                      onChange={({ detail }) =>
                        setProjectReference(detail.value)
                      }
                      placeholder="Paste an Asana project URL or GID"
                    />
                  </FormField>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      loading={workflow.isPreviewing}
                      disabled={!projectReference.trim()}
                      onClick={() => {
                        previewProject().catch(() => undefined);
                      }}
                    >
                      Find project
                    </Button>
                    {state.asanaLink && (
                      <>
                        <Button
                          onClick={() => {
                            workflow.resetAsanaPreview();
                            setEditingConnection(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          iconName="remove"
                          disabled={isJobActive}
                          onClick={() => setDisconnectVisible(true)}
                        >
                          Remove link
                        </Button>
                      </>
                    )}
                  </SpaceBetween>
                  {workflow.preview && (
                    <>
                      <KeyValuePairs
                        columns={1}
                        items={[
                          {
                            label: "Project",
                            value: (
                              <Link
                                href={workflow.preview.project.url}
                                external
                              >
                                {workflow.preview.project.name}
                              </Link>
                            ),
                          },
                        ]}
                      />
                      <Box float="right">
                        <Button
                          variant="primary"
                          loading={workflow.isSavingLink}
                          onClick={() => {
                            saveConnection().catch(() => undefined);
                          }}
                        >
                          Connect
                        </Button>
                      </Box>
                    </>
                  )}
                </SpaceBetween>
              </Form>
            </Container>
          ) : (
            <section className="chat-uat__project-card">
              <div className="chat-uat__asana-mark" aria-hidden="true">
                <span />
                <span />
              </div>
              <div className="chat-uat__project-copy">
                <div className="chat-uat__eyebrow">
                  Asana project
                  <span className="chat-uat__connected">Connected</span>
                </div>
                <Link href={state.asanaLink?.projectUrl} external>
                  {state.asanaLink?.projectName}
                </Link>
              </div>
              <div className="chat-uat__project-actions">
                <Button
                  iconName="edit"
                  onClick={() => {
                    workflow.resetAsanaPreview();
                    setProjectReference(state.asanaLink?.projectUrl ?? "");
                    setEditingConnection(true);
                  }}
                >
                  Change project
                </Button>
                <Button
                  iconName="external"
                  href={state.asanaLink?.projectUrl}
                  target="_blank"
                >
                  Open Asana
                </Button>
              </div>
            </section>
          )}

          <ChatUatPage ref={chatRef} embedded />

          {workflow.jobError && (
            <Alert
              type="warning"
              header="Validation did not complete"
              action={
                state.asanaLink &&
                workflow.isAsanaAuthorized &&
                !isJobActive ? (
                  <Button
                    onClick={() => {
                      if (workflow.canResumeValidation) {
                        workflow.resumeValidation();
                      } else {
                        workflow.startValidation().catch(() => undefined);
                      }
                    }}
                  >
                    {workflow.canResumeValidation
                      ? "Retry status"
                      : "Start again"}
                  </Button>
                ) : undefined
              }
            >
              {workflow.jobError}
            </Alert>
          )}

          {isJobActive && jobProgress && (
            <Container>
              <ProgressBar
                value={
                  jobProgress.total === 0
                    ? 0
                    : Math.round(
                        (jobProgress.completed / jobProgress.total) * 100
                      )
                }
                label="Validating test cases"
                description={`${jobProgress.completed} of ${jobProgress.total}`}
                additionalInfo={
                  jobProgress.phase === "loading"
                    ? "Reading incomplete tasks from the Asana project"
                    : "Comparing expected scope with observed order facts"
                }
              />
            </Container>
          )}

          <section
            className="chat-uat__validation"
            aria-labelledby="uat-validation-title"
          >
            <header className="chat-uat__validation-header">
              <div>
                <h2 id="uat-validation-title">UAT validation</h2>
                <p>
                  {state.asanaLink?.projectName ?? "No Asana project selected"}
                </p>
              </div>
              <Button
                variant="primary"
                iconName="play"
                loading={isJobActive}
                disabled={
                  !workflow.isAsanaAuthorized || !state.asanaLink || isJobActive
                }
                onClick={() => {
                  workflow.startValidation().catch(() => undefined);
                }}
              >
                Validate
              </Button>
            </header>
            {activeRun ? (
              <>
                <div className="chat-uat__validation-summary">
                  <span>
                    <strong>{cases.length} cases</strong> · completed{" "}
                    {new Date(activeRun.completedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span>
                    <strong>{assessmentCounts.matches_scope} matched</strong> ·{" "}
                    {attentionCount} need attention
                  </span>
                </div>
                <div className="chat-uat__case-list">
                  {sortedCases.map((item) => (
                    <div
                      key={item.taskGid}
                      className={`chat-uat__case-row ${
                        item.taskGid === selectedTaskGid
                          ? "chat-uat__case-row--selected"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="chat-uat__case-main"
                        onClick={() => investigate(item)}
                      >
                        <strong>{item.taskName}</strong>
                        <span>
                          {displayValue(item.fields.territory?.expected)} ·{" "}
                          {displayValue(item.fields.product?.expected)} /{" "}
                          {displayValue(item.fields.contentType?.expected)} ·{" "}
                          {item.orderId ?? "Order not specified"}
                        </span>
                      </button>
                      <div className="chat-uat__case-outcome">
                        {verdictIndicator(item)}
                        <Button
                          variant="inline-link"
                          onClick={() => setSelectedCase(item)}
                        >
                          Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="chat-uat__validation-empty">
                {state.asanaLink
                  ? "Select Validate to compare the current Asana test cases with placed orders."
                  : "Connect an Asana project to begin."}
              </div>
            )}
          </section>
        </SpaceBetween>
      </ContentLayout>

      <Modal
        visible={Boolean(selectedCase)}
        onDismiss={() => setSelectedCase(undefined)}
        header={selectedCase?.taskName ?? "Test-case details"}
        closeAriaLabel="Close test-case details"
        footer={
          selectedCase ? (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="external"
                  href={selectedCase.taskUrl}
                  target="_blank"
                >
                  Open Asana
                </Button>
                <Button
                  variant="primary"
                  iconName="gen-ai"
                  onClick={() => investigate(selectedCase)}
                >
                  Investigate in UAT Insights
                </Button>
              </SpaceBetween>
            </Box>
          ) : undefined
        }
      >
        {selectedCase && (
          <SpaceBetween size="m">
            <KeyValuePairs
              columns={2}
              items={[
                {
                  label: "Order",
                  value: selectedCase.orderId ?? "Not specified",
                },
                {
                  label: "Assessment",
                  value: verdictIndicator(selectedCase),
                },
                {
                  label: "Next step",
                  value: nextStep(selectedCase),
                },
                {
                  label: "Retest",
                  value: selectedCase.retestRecommended
                    ? "Recommended"
                    : "Not required",
                },
              ]}
            />
            {selectedCase.scopeErrors.length > 0 && (
              <Alert type="warning" header="Incomplete Asana task">
                {selectedCase.scopeErrors.join("; ")}
              </Alert>
            )}
            <Table
              items={fieldRows}
              trackBy={(item) => item[0]}
              columnDefinitions={[
                {
                  id: "field",
                  header: "Field",
                  cell: ([name]) => name,
                },
                {
                  id: "expected",
                  header: "Expected",
                  cell: ([, field]) => displayValue(field.expected),
                },
                {
                  id: "observed",
                  header: "Observed",
                  cell: ([, field]) => displayValue(field.observed),
                },
                {
                  id: "result",
                  header: "Result",
                  cell: ([, field]) => fieldIndicator(field.verdict),
                },
                {
                  id: "reason",
                  header: "Reason",
                  cell: ([, field]) => field.reason,
                },
              ]}
              empty={
                <Box color="text-body-secondary">
                  No field comparison is available.
                </Box>
              }
            />
          </SpaceBetween>
        )}
      </Modal>

      <Modal
        visible={disconnectVisible}
        onDismiss={() => setDisconnectVisible(false)}
        header="Remove Asana link?"
        closeAriaLabel="Close remove-link confirmation"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setDisconnectVisible(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={workflow.isDisconnecting}
                onClick={() => {
                  disconnect().catch(() => undefined);
                }}
              >
                Remove link
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            This removes the selected project. Your Asana authorization and
            completed validation history remain available.
          </Box>
          {workflow.disconnectError && (
            <Alert type="error">{workflow.disconnectError}</Alert>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
}
